import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { Obra } from '../services/types';
import { generatePdf, AnexoMode } from '../services/pdfGenerator';

export type ReportJobStatus = 'processing' | 'completed' | 'error' | 'cancelled';

export interface ReportJob {
    id: string;
    obraId: number;
    obraNome: string;
    reportType: string;
    reportLabel: string;
    includeAnexos: boolean;
    anexoMode: AnexoMode;
    status: ReportJobStatus;
    progress: number;
    statusText: string;
    errorMessage?: string;
    fileName?: string;
    blobUrl?: string;
    createdAt: number;
}

export interface StartReportJobParams {
    obra: Obra;
    reportData: any[];
    reportType: string;
    reportLabel: string;
    includeAnexos: boolean;
    anexoMode: AnexoMode;
    periodo?: { dataInicio?: string; dataFim?: string };
}

interface ReportJobsContextValue {
    jobs: ReportJob[];
    activeJobsCount: number;
    isMinimized: boolean;
    setIsMinimized: React.Dispatch<React.SetStateAction<boolean>>;
    toastNotification: { open: boolean; message: string; severity: 'success' | 'info' | 'error' };
    closeToastNotification: () => void;
    startReportJob: (params: StartReportJobParams) => string;
    cancelReportJob: (jobId: string) => void;
    dismissReportJob: (jobId: string) => void;
    clearFinishedJobs: () => void;
    downloadJobFile: (jobId: string) => void;
}

const ReportJobsContext = createContext<ReportJobsContextValue | undefined>(undefined);

const triggerBrowserDownload = (blobUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const ReportJobsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [jobs, setJobs] = useState<ReportJob[]>([]);
    const [isMinimized, setIsMinimized] = useState(false);
    const [toastNotification, setToastNotification] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'info' | 'error';
    }>({
        open: false,
        message: '',
        severity: 'info',
    });

    const controllersRef = useRef<Map<string, AbortController>>(new Map());
    const blobUrlsRef = useRef<Map<string, string>>(new Map());

    const activeJobsCount = jobs.filter((j) => j.status === 'processing').length;

    // Evita que o usuário feche a aba do navegador acidentalmente enquanto um PDF está sendo gerado
    useEffect(() => {
        if (activeJobsCount === 0) return;
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [activeJobsCount]);

    // Limpa Blob URLs ao desmontar o provider
    useEffect(() => {
        const currentBlobUrls = blobUrlsRef.current;
        return () => {
            currentBlobUrls.forEach((url) => URL.revokeObjectURL(url));
            currentBlobUrls.clear();
        };
    }, []);

    const closeToastNotification = useCallback(() => {
        setToastNotification((prev) => ({ ...prev, open: false }));
    }, []);

    const startReportJob = useCallback((params: StartReportJobParams): string => {
        const jobId = `job_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        const controller = new AbortController();
        controllersRef.current.set(jobId, controller);

        const newJob: ReportJob = {
            id: jobId,
            obraId: params.obra.id,
            obraNome: params.obra.nome,
            reportType: params.reportType,
            reportLabel: params.reportLabel,
            includeAnexos: params.includeAnexos,
            anexoMode: params.anexoMode,
            status: 'processing',
            progress: 2,
            statusText: 'Iniciando processamento em segundo plano...',
            createdAt: Date.now(),
        };

        setJobs((prev) => [newJob, ...prev]);
        setIsMinimized(false);

        // Executa de forma assíncrona e desacoplada da página atual
        (async () => {
            try {
                const { blob, fileName } = await generatePdf(
                    params.obra,
                    params.reportData,
                    params.reportType,
                    params.includeAnexos,
                    (progress) => {
                        setJobs((prev) =>
                            prev.map((j) => (j.id === jobId ? { ...j, progress } : j))
                        );
                    },
                    params.periodo,
                    {
                        anexoMode: params.anexoMode,
                        signal: controller.signal,
                        skipAutoSave: true,
                        onStatusText: (statusText) => {
                            setJobs((prev) =>
                                prev.map((j) => (j.id === jobId ? { ...j, statusText } : j))
                            );
                        },
                    }
                );

                const blobUrl = URL.createObjectURL(blob);
                blobUrlsRef.current.set(jobId, blobUrl);

                setJobs((prev) =>
                    prev.map((j) =>
                        j.id === jobId
                            ? {
                                  ...j,
                                  status: 'completed',
                                  progress: 100,
                                  statusText: 'Concluído! Download iniciado.',
                                  fileName,
                                  blobUrl,
                              }
                            : j
                    )
                );

                triggerBrowserDownload(blobUrl, fileName);

                setToastNotification({
                    open: true,
                    message: `Relatório PDF de "${params.obra.nome}" concluído! O download foi iniciado automaticamente.`,
                    severity: 'success',
                });
            } catch (err: any) {
                if (err?.name === 'AbortError' || controller.signal.aborted) {
                    setJobs((prev) =>
                        prev.map((j) =>
                            j.id === jobId
                                ? {
                                      ...j,
                                      status: 'cancelled',
                                      statusText: 'Cancelado pelo usuário.',
                                  }
                                : j
                        )
                    );
                } else {
                    console.error('Erro no processamento em segundo plano do PDF:', err);
                    const errorMessage = err?.message || 'Falha ao gerar o relatório PDF.';
                    setJobs((prev) =>
                        prev.map((j) =>
                            j.id === jobId
                                ? {
                                      ...j,
                                      status: 'error',
                                      statusText: 'Erro ao gerar PDF.',
                                      errorMessage,
                                  }
                                : j
                        )
                    );
                    setToastNotification({
                        open: true,
                        message: `Erro ao gerar relatório de "${params.obra.nome}": ${errorMessage}`,
                        severity: 'error',
                    });
                }
            } finally {
                controllersRef.current.delete(jobId);
            }
        })();

        return jobId;
    }, []);

    const cancelReportJob = useCallback((jobId: string) => {
        const controller = controllersRef.current.get(jobId);
        if (controller) {
            controller.abort();
        }
    }, []);

    const dismissReportJob = useCallback((jobId: string) => {
        const controller = controllersRef.current.get(jobId);
        if (controller) {
            controller.abort();
            controllersRef.current.delete(jobId);
        }
        const blobUrl = blobUrlsRef.current.get(jobId);
        if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
            blobUrlsRef.current.delete(jobId);
        }
        setJobs((prev) => prev.filter((j) => j.id !== jobId));
    }, []);

    const clearFinishedJobs = useCallback(() => {
        setJobs((prev) => {
            prev.forEach((j) => {
                if (j.status !== 'processing') {
                    const url = blobUrlsRef.current.get(j.id);
                    if (url) {
                        URL.revokeObjectURL(url);
                        blobUrlsRef.current.delete(j.id);
                    }
                }
            });
            return prev.filter((j) => j.status === 'processing');
        });
    }, []);

    const downloadJobFile = useCallback(
        (jobId: string) => {
            const job = jobs.find((j) => j.id === jobId);
            if (job?.blobUrl && job?.fileName) {
                triggerBrowserDownload(job.blobUrl, job.fileName);
            }
        },
        [jobs]
    );

    return (
        <ReportJobsContext.Provider
            value={{
                jobs,
                activeJobsCount,
                isMinimized,
                setIsMinimized,
                toastNotification,
                closeToastNotification,
                startReportJob,
                cancelReportJob,
                dismissReportJob,
                clearFinishedJobs,
                downloadJobFile,
            }}
        >
            {children}
        </ReportJobsContext.Provider>
    );
};

export const useReportJobs = (): ReportJobsContextValue => {
    const context = useContext(ReportJobsContext);
    if (!context) {
        throw new Error('useReportJobs deve ser utilizado dentro de um ReportJobsProvider');
    }
    return context;
};
